import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import aiService from '../services/aiService';
import offlineApi from '../services/offlineApi';
import itemService from '../services/itemService';
import { invalidateSearchCache } from '../utils/searchCache';
import { placeholderImage, getBestItemImageUrl } from '../utils/imageUtils';
import OfflineImage from '../components/OfflineImage';
import TagExtractionStatus from '../components/TagExtractionStatus';
import AIProcessingIndicator from '../components/AIProcessingIndicator';
import CreateBoxDialog from '../components/CreateBoxDialog';

const ItemEdit = () => {
  const navigate = useAppNavigation();
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [manualTagInput, setManualTagInput] = useState('');
  const [extractedTags, setExtractedTags] = useState([]);
  const [manualTags, setManualTags] = useState([]);
  const [tagsExtracted, setTagsExtracted] = useState(false);
  const [selectedBox, setSelectedBox] = useState('');
  const [createBoxOpen, setCreateBoxOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [tagExtractionTask, setTagExtractionTask] = useState(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // State for fullscreen image dialog
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  // State for related items (shared group photo)
  const [relatedItems, setRelatedItems] = useState(null);
  const [groupImageDialogOpen, setGroupImageDialogOpen] = useState(false);
  
  // Helper function to add manual tags from input
  const handleAddManualTags = () => {
    // Split input by commas, trim, and filter empty ones
    const newTags = manualTagInput.split(',').map(t => t.trim()).filter(t => t);
    if (newTags.length === 0) return;
    
    // Add to manual tags, avoiding duplicates
    setManualTags(prev => {
      const combined = [...prev, ...newTags];
      // Remove duplicates - case insensitive
      return Array.from(new Set(combined.map(t => t.toLowerCase())))
        .map(t => {
          // Find original case version
          const original = combined.find(o => o.toLowerCase() === t);
          return original;
        });
    });
    setManualTagInput('');
  };

  // Fetch item details and boxes on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Check if offline mode is enabled or if device is actually offline
        const isOfflineMode = await offlineApi.isOfflineModeEnabled();
        const isActuallyOffline = !navigator.onLine;
        console.log(`ItemEdit: Offline mode status - enabled: ${isOfflineMode}, actually offline: ${isActuallyOffline}`);
        
        // Determine which API to use based on connection status
        const useOfflineApi = isOfflineMode || isActuallyOffline;
        console.log(`ItemEdit: Using ${useOfflineApi ? 'offline' : 'online'} API`);
        
        let itemData = null;
        let boxesData = [];
        
        if (useOfflineApi) {
          // Get data from offline storage
          try {
            console.log(`ItemEdit: Fetching item ${itemId} from offline storage`);
            
            // First try to get all boxes to find the item in the box's items array
            const allBoxes = await offlineApi.getAllBoxes();
            if (Array.isArray(allBoxes)) {
              // Look through all boxes for the item in their items arrays
              for (const box of allBoxes) {
                if (Array.isArray(box.items)) {
                  const foundItem = box.items.find(item => item.id === itemId);
                  if (foundItem) {
                    itemData = foundItem;
                    console.log(`ItemEdit: Found item in box ${box.id}'s items array:`, itemData);
                    break;
                  }
                }
              }
            }
            
            // If not found in box items arrays, check the items store
            if (!itemData) {
              // Get all items from offline storage
              const allItems = await offlineApi.getAllItems();
              
              // Find the specific item we're looking for
              if (Array.isArray(allItems)) {
                itemData = allItems.find(item => item.id === itemId);
                if (itemData) {
                  console.log(`ItemEdit: Found item in items store:`, itemData);
                }
              }
            }
            
            if (!itemData) {
              console.error(`ItemEdit: Item ${itemId} not found in offline storage`);
            }
            
            // Get all boxes for the dropdown
            boxesData = await offlineApi.getAllBoxes();
            console.log(`ItemEdit: Found ${boxesData.length} boxes in offline storage`);
          } catch (offlineError) {
            console.error('ItemEdit: Error fetching from offline storage:', offlineError);
            throw offlineError; // Re-throw to be caught by outer catch
          }
        } else {
          // Online mode - use regular API
          try {
            // Fetch item details from server
            const itemResponse = await api.get(`/items/${itemId}`);
            itemData = itemResponse.data;
            console.log('ItemEdit: Item data from API:', itemData);
            
            // Cache image for offline use if online
            if (itemData) {
              try {
                // Image caching is now handled by the updated offline API
                // No need to manually cache images
                console.log('ItemEdit: Cached item images for offline use');
              } catch (cacheError) {
                console.warn('ItemEdit: Failed to cache item images:', cacheError);
                // Non-critical error, continue with the flow
              }
            }
            
            // Fetch boxes for the dropdown
            const boxesResponse = await api.get('/boxes');
            boxesData = boxesResponse.data || [];
            console.log(`ItemEdit: Found ${boxesData.length} boxes from API`);
          } catch (apiError) {
            console.error('ItemEdit: Error fetching from API:', apiError);
            throw apiError; // Re-throw to be caught by outer catch
          }
        }
        
        // Set data if found
        if (itemData) {
          setItem(itemData);
          
          // Set form values from item data
          setName(itemData.name || '');
          setDescription(itemData.description || '');
          // Use box_id, or fall back to album_id for backward compatibility
          setSelectedBox(itemData.box_id || itemData.album_id || '');
          
          // Check if tags_extracted is set in the item data
          setTagsExtracted(!!itemData.tags_extracted);
          
          // Parse any existing tags
          if (itemData.custom_tags) {
            // Check if custom_tags is a string before trying to split it
            const tagArray = typeof itemData.custom_tags === 'string' 
              ? itemData.custom_tags.split(',').map(tag => tag.trim()).filter(tag => tag)
              : Array.isArray(itemData.custom_tags) 
                ? itemData.custom_tags.map(tag => tag.trim()).filter(tag => tag) 
                : [];
            
            // If tags have been extracted before, split between extracted and manual tags
            if (itemData.tags_extracted) {
              // For items with tags_extracted=true, we need to check if there were any manual tags added previously
              // We'll get manual tags from a separate API call or assume they're all extracted for now
              setExtractedTags(tagArray);
              setManualTags([]); // Start with empty manual tags
            } else {
              // If tags have not been extracted, all are manual
              setManualTags(tagArray);
              setExtractedTags([]);
            }
          }
          
          // Set boxes for dropdown
          if (Array.isArray(boxesData)) {
            setBoxes(boxesData);
          }
          
          setError(null);
        } else {
          // If item wasn't found, show error
          console.error(`ItemEdit: Item ${itemId} not found`);
          setError('Item not found. Please check your connection or try again.');
        }
      } catch (error) {
        console.error('ItemEdit: Error fetching item data:', error);
        setError('Failed to load item data. Please check your connection or try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (itemId) {
      fetchData();
    }
  }, [itemId]);
  
  // Fetch related items (for shared group photo)
  useEffect(() => {
    const fetchRelatedItems = async () => {
      try {
        if (!itemId) return;
        const response = await api.get(`/items/${itemId}/related`);
        const relatedData = response.data;
        console.log('ItemEdit: Related items data:', relatedData);
        
        // Construct proxy URL from shared_image_id
        if (relatedData.shared_image_id && relatedData.user_id) {
          const baseUrl = process.env.REACT_APP_API_URL || '';
          relatedData.shared_image_url = `${baseUrl}/api/v2/files/shared/${relatedData.shared_image_id}/${relatedData.user_id}`;
          console.log('ItemEdit: Constructed shared_image_url:', relatedData.shared_image_url);
        }
        
        setRelatedItems(relatedData);
      } catch (error) {
        console.error('Error fetching related items:', error);
        setRelatedItems(null);
      }
    };
    
    if (itemId && item) {
      fetchRelatedItems();
    }
  }, [itemId, item]);
  
  // Poll for AI processing status updates when item is being processed
  useEffect(() => {
    if (!item) return;
    
    const isProcessing = item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing';
    
    if (isProcessing) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/items/${itemId}`);
          const updatedItem = response.data;
          
          // Check if processing status changed
          if (updatedItem.ai_processing_status !== item.ai_processing_status) {
            console.log('ItemEdit: AI processing status changed:', updatedItem.ai_processing_status);
            setItem(updatedItem);
            
            // Update form fields if processing completed
            if (updatedItem.ai_processing_status === 'completed') {
              if (updatedItem.description) {
                setDescription(updatedItem.description);
              }
              if (updatedItem.custom_tags) {
                const tags = typeof updatedItem.custom_tags === 'string' 
                  ? updatedItem.custom_tags.split(',').map(t => t.trim()).filter(t => t)
                  : updatedItem.custom_tags;
                setExtractedTags(tags);
                setTagsExtracted(true);
              }
            }
          }
        } catch (err) {
          console.error('Error polling AI processing status:', err);
        }
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [item, itemId]);
  
  // Handle form submission to update item
  const handleBoxCreated = (box) => {
    setBoxes((prev) => [...prev, box]);
    setSelectedBox(box.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Check if offline mode is enabled or if device is actually offline
      const isOfflineMode = await offlineApi.isOfflineModeEnabled();
      const isActuallyOffline = !navigator.onLine;
      const useOfflineApi = isOfflineMode || isActuallyOffline;
      console.log(`ItemEdit: Saving in ${useOfflineApi ? 'offline' : 'online'} mode`);
      
      // Combine extracted and manual tags
      const uniqueTags = new Set([...extractedTags, ...manualTags].map(tag => tag.toLowerCase()));
      const allTags = Array.from(uniqueTags)
        .map(tag => {
          // Find original case in either array
          const fromExtracted = extractedTags.find(t => t.toLowerCase() === tag);
          const fromManual = manualTags.find(t => t.toLowerCase() === tag);
          return fromManual || fromExtracted; // Prefer manual version if available
        })
        .join(',');
      
      // Prepare data
      const updateData = {
        ...item, // Preserve existing item data
        name,
        description,
        custom_tags: allTags,
        box_id: selectedBox || null,
        tags_extracted: tagsExtracted
      };
      
      if (useOfflineApi) {
        try {
          // Queue update for when back online
          await offlineApi.queueRequest({
            method: 'PUT',
            url: `/items/${itemId}`,
            data: updateData,
            timestamp: Date.now(),
            type: 'item_update'
          });
          
          // Update the item in offline storage via offlineApi
          await offlineApi.updateItem(itemId, updateData);
          console.log(`ItemEdit: Item updated in offline mode`);
        } catch (offlineError) {
          console.error('ItemEdit: Error updating in offline mode:', offlineError);
          throw offlineError; // Re-throw to be caught by outer catch
        }
      } else {
        // Update item directly through API
        await api.put(`/items/${itemId}`, updateData);
        console.log(`ItemEdit: Item updated through API`);
      }
      
      // Show success message
      setSuccess(true);
      
      // Redirect back to box page if we have a box ID
      if (selectedBox) {
        console.log(`Redirecting to box detail page: /box/${selectedBox}`);
        // Brief delay to show success message before redirecting
        setTimeout(() => {
          navigate.to.BOX_DETAIL(selectedBox);
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error updating item:', error);
      setError('Failed to update item. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Delete item
  const deleteItem = async () => {
    try {
      setIsDeleting(true);
      
      // Check if offline mode is enabled or if device is actually offline
      const isOfflineMode = await offlineApi.isOfflineModeEnabled();
      const isActuallyOffline = !navigator.onLine;
      const useOfflineApi = isOfflineMode || isActuallyOffline;
      console.log(`ItemEdit: Deleting in ${useOfflineApi ? 'offline' : 'online'} mode`);
      
      if (useOfflineApi) {
        // Queue delete for when back online
        await offlineApi.queueRequest({
          method: 'DELETE',
          url: `/items/${itemId}`,
          data: null,
          timestamp: Date.now(),
          type: 'item_delete'
        });
        console.log(`ItemEdit: Queued item deletion for later sync`);
        
        // Remove from offline storage using offlineApi
        try {
          // Delete the item using offlineApi
          await offlineApi.deleteItem(itemId);
          console.log(`ItemEdit: Removed item from offline storage`);
        } catch (storageError) {
          console.error('ItemEdit: Error removing item from offline storage:', storageError);
        }
        // Invalidate search cache immediately when deleting offline
        console.log('ItemEdit: Invalidating search cache for offline deletion');
        const newVersion = invalidateSearchCache();
        console.log(`ItemEdit: Cache invalidated, new version: ${newVersion}`);
      } else {
        // Delete directly through API using itemService (which invalidates cache)
        console.log(`ItemEdit: Deleting item ${itemId} through API`);
        await itemService.deleteItem(itemId);
        console.log(`ItemEdit: Item deleted through API, cache should be invalidated by service`);
      }
      
      setDeleteDialogOpen(false);
      
      // Navigate back (to where user came from)
      navigate(-1);
      
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete item. Please try again.');
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  
  // Periodically check tag extraction task status
  useEffect(() => {
    const pollTaskStatus = async () => {
      if (!tagExtractionTask?.task_id) return;
      
      try {
        const status = await aiService.checkTagExtractionStatus(tagExtractionTask.task_id);
        console.log('Tag extraction status:', status);
        
        // Update task status
        setTagExtractionTask(prev => ({ ...prev, ...status }));
        
        // If completed, fetch the tags
        if (status.status === 'completed') {
          const tagsResponse = await aiService.getItemTags(itemId);
          if (tagsResponse && tagsResponse.tags) {
            setExtractedTags(tagsResponse.tags);
            setTagsExtracted(true);
            setTagExtractionTask(null); // Clear task once completed
          }
          setIsExtracting(false);
        } else if (status.status === 'failed') {
          setError(`Failed to extract tags: ${status.error || 'Unknown error'}`);
          setIsExtracting(false);
          setTagExtractionTask(null); // Clear failed task
        }
      } catch (err) {
        console.error('Error checking tag status:', err);
        // Don't set error here - just log it and we'll keep trying
      }
    };
    
    let pollInterval = null;
    // If we have an active task and it's still in progress, poll for updates
    if (tagExtractionTask?.task_id && 
        (tagExtractionTask.status === 'pending' || tagExtractionTask.status === 'running')) {
      pollInterval = setInterval(pollTaskStatus, 2000); // Poll every 2 seconds
    }
    
    // Initial check immediately when task is created
    if (tagExtractionTask?.task_id && !pollInterval) {
      pollTaskStatus();
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [tagExtractionTask, itemId]);
  
  // Extract tags from the image using AI
  const extractTags = async () => {
    try {
      setIsExtracting(true);
      setError(null);
      
      // Queue tag extraction in background worker
      const response = await aiService.queueTagExtraction(itemId);
      
      if (response && response.task_id) {
        console.log('Tag extraction queued:', response);
        setTagExtractionTask({ 
          task_id: response.task_id,
          item_id: itemId,
          status: 'pending',
          timestamp: Date.now()
        });
      } else {
        throw new Error('No task ID returned for tag extraction');
      }
    } catch (err) {
      console.error('Error queueing tag extraction:', err);
      setError('Failed to queue tag extraction. Please try again.');
      setIsExtracting(false);
    }
  };
  
  // Generate description and extract tags using combined AI service (recommended)
  const generateDescriptionAndTags = async () => {
    try {
      setIsGeneratingDescription(true);
      setIsExtracting(true);
      setError(null);
      
      console.log('Queuing AI processing for description and tags...');
      
      // Queue the processing - returns immediately
      const queueResponse = await aiService.extractDescriptionAndTags(itemId);
      console.log('Queue response:', queueResponse);
      
      // If processing is queued, poll for completion
      if (queueResponse.status === 'processing') {
        console.log('AI processing queued, polling for completion...');
        
        const result = await aiService.pollItemStatus(itemId, (status) => {
          console.log(`AI processing status: ${status}`);
        });
        
        if (result.status === 'completed') {
          if (result.description) {
            setDescription(result.description);
          }
          if (result.tags && Array.isArray(result.tags)) {
            setExtractedTags(result.tags);
            setTagsExtracted(true);
          }
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
        } else if (result.status === 'content_rejected') {
          setError('Image content was rejected. Please use a different image.');
        }
      } else if (queueResponse.description) {
        // Legacy: synchronous response
        setDescription(queueResponse.description);
        if (queueResponse.tags && Array.isArray(queueResponse.tags)) {
          setExtractedTags(queueResponse.tags);
          setTagsExtracted(true);
        }
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error generating description and extracting tags:', err);
      setError('Failed to generate description and extract tags. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
      setIsExtracting(false);
    }
  };

  // Generate description using AI (legacy method)
  const generateAIDescription = async () => {
    try {
      setIsGeneratingDescription(true);
      setError(null);
      
      // Call AI service to generate description
      const response = await aiService.generateDescription(itemId);
      
      if (response && response.description) {
        setDescription(response.description);
        setSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error('No description returned from AI service');
      }
    } catch (err) {
      console.error('Error generating description:', err);
      setError('Failed to generate AI description. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };
  
  // Generate display of tags as chips
  const renderTagChips = () => {
    const hasExtractedTags = extractedTags.length > 0;
    const hasManualTags = manualTags.length > 0;
    
    if (!hasExtractedTags && !hasManualTags) return null;
    
    return (
      <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
        {/* Extracted tags section - blue chips */}
        {hasExtractedTags && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Extracted Tags:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {extractedTags.map((tag, index) => (
                <Chip 
                  key={`extracted-${index}`} 
                  label={tag} 
                  size="small" 
                  color="primary"
                  variant="outlined" 
                  onDelete={() => {
                    // Remove this tag from extracted tags
                    const newTags = [...extractedTags];
                    newTags.splice(index, 1);
                    setExtractedTags(newTags);
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
        
        {/* Manual tags section - purple chips */}
        {hasManualTags && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: hasExtractedTags ? 1 : 0 }}>
              Manual Tags:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {manualTags.map((tag, index) => (
                <Chip 
                  key={`manual-${index}`} 
                  label={tag} 
                  size="small" 
                  color="secondary"
                  variant="outlined" 
                  onDelete={() => {
                    // Remove this tag from manual tags
                    const newTags = [...manualTags];
                    newTags.splice(index, 1);
                    setManualTags(newTags);
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    );
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={() => navigate(-1)} // Go back to previous page
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {isLoading ? 'Loading...' : item ? `Edit Item` : 'Item Details'}
          </Typography>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Item updated successfully!
          </Alert>
        )}
        
        {/* AI Processing Indicator - Shows when item is being processed */}
        {item && (item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && (
          <AIProcessingIndicator 
            status={item.ai_processing_status}
            isGroupPhoto={relatedItems?.has_shared_image || false}
            groupPhotoItemCount={relatedItems?.total_items || 0}
            variant="banner"
            sx={{ mb: 2 }}
          />
        )}
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : item ? (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Item Image */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box 
                  component="div" 
                  onClick={() => setImageDialogOpen(true)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.9 },
                    position: 'relative'
                  }}
                >
                  <OfflineImage 
                    src={getBestItemImageUrl(item)}
                    itemId={item.id}
                    alt={item.name || 'Item image'}
                    fallbackSrc={placeholderImage}
                    sx={{ 
                      width: '100%', 
                      height: 'auto',
                      maxHeight: 300,
                      objectFit: 'contain'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <Typography variant="subtitle1" color="white" sx={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '4px 8px', borderRadius: '4px' }}>
                      Click to view full size
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
            
            {/* Group Photo Section - shows when this item was extracted from a group photo */}
            {relatedItems && relatedItems.has_shared_image && (
              <Paper
                elevation={0}
                sx={{ 
                  p: 2, 
                  mb: 3,
                  borderRadius: 2, 
                  backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  border: '1px solid rgba(255, 152, 0, 0.3)'
                }}
              >
                <Typography variant="body1" sx={{ color: '#e65100', fontWeight: 600, mb: 1 }}>
                  📷 This item was extracted from a group photo ({relatedItems.total_items || 1} items total)
                </Typography>
                {relatedItems.items_in_same_box && relatedItems.items_in_same_box.length > 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    + {relatedItems.items_in_same_box.length} other item(s) from the same photo in this box
                  </Typography>
                )}
                {relatedItems.items_in_other_boxes && relatedItems.items_in_other_boxes.length > 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    + {relatedItems.items_in_other_boxes.length} item(s) from the same photo in other boxes
                  </Typography>
                )}
                {relatedItems.shared_image_url && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={() => setGroupImageDialogOpen(true)}
                      sx={{ borderRadius: 2, borderColor: '#e65100', color: '#e65100' }}
                    >
                      View original group photo
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
            
            {/* Item Details Form */}
            <Paper sx={{ p: 3 }}>
              {/* Box Selection */}
              <FormControl fullWidth margin="normal">
                <InputLabel id="box-select-label">Box</InputLabel>
                <Select
                  labelId="box-select-label"
                  id="box-select"
                  value={selectedBox}
                  label="Box"
                  onChange={(e) => {
                    if (e.target.value === '__create__') {
                      setCreateBoxOpen(true);
                      return; // don't store the sentinel as the selection
                    }
                    setSelectedBox(e.target.value);
                  }}
                  disabled={isSaving}
                >
                  <MenuItem value="">
                    <em>None (Unassigned)</em>
                  </MenuItem>
                  {boxes.map((box) => (
                    <MenuItem key={box.id} value={box.id}>
                      {box.name}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="__create__" sx={{ color: 'primary.main' }}>
                    ＋ Create new box…
                  </MenuItem>
                </Select>
              </FormControl>
              
              {/* Name */}
              <TextField
                fullWidth
                margin="normal"
                label="Item Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
              
              {/* Description */}
              {/* Description with AI Button */}
              <Box sx={{ mb: 2 }}>
                {/* Combined AI Button - Recommended */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={(isGeneratingDescription || isExtracting) ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                    onClick={generateDescriptionAndTags}
                    disabled={isGeneratingDescription || isExtracting || isSaving}
                    color="primary"
                    sx={{ px: 3 }}
                  >
                    {(isGeneratingDescription || isExtracting) ? 'Processing...' : 'Generate Description & Tags'}
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">
                    Description
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={isGeneratingDescription ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                    onClick={generateAIDescription}
                    disabled={isGeneratingDescription || isSaving}
                    color="secondary"
                  >
                    {isGeneratingDescription ? 'Generating...' : 'Description Only'}
                  </Button>
                </Box>
                
                {/* Show inline processing indicator when AI is auto-generating */}
                {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && !description ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    p: 2, 
                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                    borderRadius: 1,
                    border: '1px solid rgba(33, 150, 243, 0.3)'
                  }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      AI is generating description...
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isSaving || isGeneratingDescription}
                    placeholder="Enter description or use AI to generate one"
                  />
                )}
              </Box>
              
              {/* Tags Section - Hidden from UI but still functional */}
              <Box sx={{ mt: 3, display: 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">
                    Tags
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocalOfferIcon />}
                    onClick={extractTags}
                    disabled={isExtracting || isSaving}
                    color="secondary"
                  >
                    {isExtracting ? <CircularProgress size={24} /> : 'Tags Only'}
                  </Button>
                </Box>
                
                <TextField
                  fullWidth
                  margin="normal"
                  label="Add Manual Tags (comma separated)"
                  placeholder="Enter tags and press Enter"
                  value={manualTagInput || ''}
                  onChange={(e) => setManualTagInput(e.target.value)}
                  disabled={isSaving || isExtracting}
                  helperText="Manual tags are preserved when re-extracting tags"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && manualTagInput.trim()) {
                      e.preventDefault();
                      // Process the input when Enter is pressed
                      handleAddManualTags();
                    }
                  }}
                  onBlur={() => {
                    // Also process input when the field loses focus
                    if (manualTagInput.trim()) {
                      handleAddManualTags();
                    }
                  }}
                />
                
                {renderTagChips()}
              </Box>
              
              {/* Display only tag chips without the input field */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Tags
                </Typography>
                {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && 
                 extractedTags.length === 0 && manualTags.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    p: 1.5, 
                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                    borderRadius: 1,
                    border: '1px solid rgba(33, 150, 243, 0.3)'
                  }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      AI is extracting tags...
                    </Typography>
                  </Box>
                ) : renderTagChips()}
              </Box>
              
              {/* Action Buttons */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                >
                  {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Alert severity="warning">
            Item not found
          </Alert>
        )}
      </Container>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={deleteItem} color="error" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Fullscreen Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'black',
            height: '100%',
            maxHeight: '100vh',
            m: 0,
            borderRadius: 0,
          }
        }}
      >
        <IconButton
          onClick={() => setImageDialogOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            overflow: 'auto',
            p: 2,
            // Use background for proper scaling of small images
            '& > *': {
              maxWidth: '95vw',
              maxHeight: 'calc(100vh - 64px)',
            }
          }}
          onClick={() => setImageDialogOpen(false)}
        >
          {item && (
            <Box
              component="img"
              src={getBestItemImageUrl(item)}
              alt={item.name || 'Item image'}
              crossOrigin="anonymous"
              onError={(e) => { e.target.src = placeholderImage; }}
              sx={{ 
                // Scale small images up to a minimum size while respecting max bounds
                minWidth: { xs: '80vw', sm: '60vw', md: '50vw' },
                minHeight: { xs: '40vh', sm: '50vh', md: '60vh' },
                maxWidth: '95vw',
                maxHeight: 'calc(100vh - 64px)',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
        </Box>
      </Dialog>
      
      {/* Group Photo Dialog */}
      <Dialog
        open={groupImageDialogOpen}
        onClose={() => setGroupImageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.9)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.8)'
        }}>
          <Typography variant="h6">Original Group Photo</Typography>
          <IconButton
            onClick={() => setGroupImageDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 2,
          backgroundColor: 'rgba(0,0,0,0.9)'
        }}>
          {relatedItems?.shared_image_url && (
            <Box
              component="img"
              src={relatedItems.shared_image_url}
              alt="Original group photo"
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Display background tag extraction status */}
      <TagExtractionStatus itemId={itemId} />

      <CreateBoxDialog
        open={createBoxOpen}
        onClose={() => setCreateBoxOpen(false)}
        onCreated={handleBoxCreated}
      />
    </Box>
  );
};

export default ItemEdit;
